import { useState, useEffect } from 'react';

const HEADER_PREFERENCE_KEY = 'headerVisibilityPreference';
const HAS_VISITED_KEY = 'hasVisitedBefore';
const EXPLORE_ANIMATION_COMPLETE_EVENT = 'exploreAnimationComplete';

const useHeaderToggle = () => {
  // Helper to check if we're on explore page
  const getIsExplorePage = () => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname === '/explore';
  };

  // Load saved preference or default to true for first visit
  const [isHeaderVisible, setIsHeaderVisible] = useState(() => {
    if (typeof window === 'undefined') return true;
    
    try {
      const hasVisited = localStorage.getItem(HAS_VISITED_KEY);
      if (!hasVisited) {
        // First visit - show header initially
        return true;
      }
      
      // Not first visit - load saved preference
      const saved = localStorage.getItem(HEADER_PREFERENCE_KEY);
      if (saved !== null) {
        return saved === 'true';
      }
    } catch (e) {
      console.warn('Failed to load header preference:', e);
    }
    return true;
  });

  const [hasAutoClosed, setHasAutoClosed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return localStorage.getItem(HAS_VISITED_KEY) === 'true';
    } catch (e) {
      return false;
    }
  });

  // Save preference to localStorage whenever it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    try {
      localStorage.setItem(HEADER_PREFERENCE_KEY, String(isHeaderVisible));
    } catch (e) {
      console.warn('Failed to save header preference:', e);
    }
  }, [isHeaderVisible]);

  // On first visit only, auto-close header
  // If on explore page, wait 4 seconds after animation completes
  // Otherwise, wait 2 seconds
  useEffect(() => {
    if (hasAutoClosed) return; // Already handled, don't set up again
    
    let timer;
    let hasHandledAnimation = false;
    let eventReceived = false;

    const hideHeader = () => {
      if (hasHandledAnimation) return; // Prevent multiple calls
      hasHandledAnimation = true;
      
      setIsHeaderVisible(false);
      setHasAutoClosed(true);
      
      // Mark that user has visited
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(HAS_VISITED_KEY, 'true');
          localStorage.setItem(HEADER_PREFERENCE_KEY, 'false');
        } catch (e) {
          console.warn('Failed to save visit status:', e);
        }
      }
    };

    const isExplorePage = getIsExplorePage();

    if (isExplorePage) {
      // On explore page - wait for animation to complete, then 4 more seconds
      // We MUST wait for the exploreAnimationComplete event - this ensures we wait
      // for the full animation (5.3 seconds) plus 4 more seconds
      const handleAnimationComplete = () => {
        if (eventReceived) return; // Prevent multiple calls
        eventReceived = true;
        
        // Clear any existing timer
        if (timer) clearTimeout(timer);
        
        // Animation completed, wait 4 more seconds before hiding header
        timer = setTimeout(() => {
          hideHeader();
        }, 4000); // 4 seconds after animation completes
      };

      // Listen for animation complete event - this is the ONLY way we should hide the header
      // The event is dispatched:
      // 1. After 5.3 seconds when animation completes (first visit)
      // 2. Immediately when animation is skipped (return visit)
      window.addEventListener(EXPLORE_ANIMATION_COMPLETE_EVENT, handleAnimationComplete);

      return () => {
        window.removeEventListener(EXPLORE_ANIMATION_COMPLETE_EVENT, handleAnimationComplete);
        if (timer) clearTimeout(timer);
      };
    } else {
      // Not on explore page - use original 2 second delay
      timer = setTimeout(hideHeader, 2000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [hasAutoClosed]);

  const toggleHeader = () => {
    setIsHeaderVisible(prev => {
      const newValue = !prev;
      // Save preference immediately when user manually toggles
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(HEADER_PREFERENCE_KEY, String(newValue));
          localStorage.setItem(HAS_VISITED_KEY, 'true'); // Mark as visited if they toggle
        } catch (e) {
          console.warn('Failed to save header preference:', e);
        }
      }
      return newValue;
    });
  };

  const showHeader = () => {
    setIsHeaderVisible(true);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(HEADER_PREFERENCE_KEY, 'true');
        localStorage.setItem(HAS_VISITED_KEY, 'true');
      } catch (e) {
        console.warn('Failed to save header preference:', e);
      }
    }
  };

  const hideHeader = () => {
    setIsHeaderVisible(false);
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(HEADER_PREFERENCE_KEY, 'false');
        localStorage.setItem(HAS_VISITED_KEY, 'true');
      } catch (e) {
        console.warn('Failed to save header preference:', e);
      }
    }
  };

  return {
    isHeaderVisible,
    toggleHeader,
    showHeader,
    hideHeader,
  };
};

export default useHeaderToggle;

